import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { TooltipButton } from "@/components/ui/TooltipButton";
import { useNavigate, useLocation } from "react-router-dom";
import { createPageUrl, getCsrfToken } from "@/utils";
import { Loader2, TrendingUp, Award, Share2 } from "lucide-react";
import { toast } from "sonner";
import ShareDialog from "@/components/ShareDialog";

export default function VotePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  const { track, feedbackData } = location.state || {};
  
  const [currentUser, setCurrentUser] = useState(null);
  const [votesToAllocate, setVotesToAllocate] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  useEffect(() => {
    document.title = "Vote for Music - Support Your Favorite Independent Artists | Soundope";
    
    const metaTags = [
      { type: 'name', key: 'description', content: "Vote for your favorite independent music tracks. Support emerging artists by allocating your monthly votes to the best submissions. Help great music rise to the top." },
      { type: 'name', key: 'keywords', content: "vote for music, support independent artists, music voting, rate music, music competition, artist voting, discover music, community voting" },
    ];

    metaTags.forEach(({ type, key, content }) => {
      let tag = document.querySelector(`meta[${type}="${key}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(type, key);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    });

    loadUser();
    if (!track) {
      navigate(createPageUrl("Discover"));
    }
  }, []);

  const loadUser = async () => {
    try {
      const { data: sessionData } = await digitalOcean.auth.getSession();
      const session = sessionData?.session;
      
      if (!session) {
        navigate(createPageUrl('Auth'));
        return;
      }

      const { data: profile } = await digitalOcean
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      
      setCurrentUser(profile);
    } catch (error) {
      console.error("Error loading user:", error);
    }
  };

  const submitVoteMutation = useMutation({
    mutationFn: async (voteCount) => {
      if (voteCount > 0) {
        // Praise/likes are unlimited - no weekly vote check needed
        // Weekly votes are only for Leaderboard voting

        // Create vote record
        const { error: voteError } = await digitalOcean
          .from('votes')
          .insert({
            track_id: track.id,
            voter_id: currentUser.id,
            vote_type: "praise"
          })
          .single();

        if (voteError) throw voteError;

        // Increment track praise count
        const { data: praiseResult, error: praiseError } = await databaseClient.rpc('increment_track_praise_count', {
          p_track_id: track.id,
          p_amount: voteCount
        });

        if (praiseError) throw praiseError;

        // Create archive log
        await digitalOcean
          .from('archive_logs')
          .insert({
            user_id: currentUser.id,
            action: 'praise_cast',
            details: {
              track_title: track.title,
              artist_name: track.artist_name,
              praise_count: voteCount
            }
          })
          .single();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['discover-tracks']);
      toast.success("Vote submitted!");
      navigate(createPageUrl("Discover"));
    },
    onError: (error) => {
      toast.error(error.message || "Failed to submit vote");
    }
  });

  const handleVote = async () => {
    setIsSubmitting(true);
    await submitVoteMutation.mutateAsync(votesToAllocate);
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    await submitVoteMutation.mutateAsync(0);
  };

  if (!track || !currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#707070]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-8 flex items-center justify-center">
      <div className="max-w-xl w-full">
        {/* Success Message */}
        {feedbackData && (
          <div className="neuro-base rounded-3xl p-6 mb-6 text-center">
            <Award className="w-12 h-12 text-[#a0a0a0] mx-auto mb-4" />
            <h2 className="text-xl font-light text-[#d0d0d0] mb-2">
              Feedback Submitted!
            </h2>
            <p className="text-sm text-[#909090] mb-2">
              +{feedbackData.points} points ({feedbackData.tier})
            </p>
          </div>
        )}

        {/* Track Info */}
        <div className="neuro-base rounded-3xl p-6 mb-6">
          <div className="flex gap-4 mb-6">
            <img
              src={track.cover_image_url}
              alt={track.title}
              className="w-24 h-24 rounded-2xl object-cover neuro-pressed"
            />
            <div className="flex-1">
              <h2 className="text-xl font-light text-[#d0d0d0] mb-1">{track.title}</h2>
              <p className="text-sm text-[#808080] mb-3">{track.artist_name}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsShareDialogOpen(true)}
                className="neuro-base active:neuro-pressed rounded-xl mt-2"
              >
                <Share2 className="h-4 w-4 mr-2" />
                <span className="text-xs">Share</span>
              </Button>
            </div>
          </div>

          <div className="neuro-pressed rounded-2xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-[#a0a0a0]" />
              <h3 className="text-sm font-medium text-[#d0d0d0]">
                Show Some Love?
              </h3>
            </div>
            <p className="text-xs text-[#808080] leading-relaxed">
              Praise this track to help it get discovered! Likes are unlimited.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <TooltipButton
            onClick={handleSkip}
            disabled={isSubmitting}
            className="neuro-base active:neuro-pressed rounded-2xl px-8 py-4 flex-1 smooth-transition"
            tooltip="Skip voting and move to next track"
          >
            <span className="text-[#808080]">Skip</span>
          </TooltipButton>
          
          <TooltipButton
            onClick={handleVote}
            disabled={isSubmitting}
            className="neuro-base active:neuro-pressed rounded-2xl px-8 py-4 flex-1 smooth-transition"
            tooltip="Give this track some praise!"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin text-[#808080]" />
                <span className="ml-2 text-[#808080]">Submitting...</span>
              </>
            ) : (
              <>
                <TrendingUp className="w-5 h-5 text-[#a0a0a0]" />
                <span className="ml-2 text-[#a0a0a0]">Vote</span>
              </>
            )}
          </TooltipButton>
        </div>

        {/* Share Dialog */}
        <ShareDialog
          isOpen={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          trackId={track?.id}
          trackTitle={track?.title}
          artistName={track?.artist_name}
        />
      </div>
    </div>
  );
}
